<script lang="ts" setup>
import {
  renderActivityTitle,
  formatActivityTimestamp,
  activityIconClass,
  activityColorClass,
} from "@/queries/activities";
import type { Activity } from "@/types";

/**
 * Standard "Recent Activity" panel — used on the Product detail, Project history tab, and
 * User profile timeline. Renders an Activity[] as the canonical icon + title + timestamp list.
 *
 * The caller is responsible for fetching (typically via `useActivities({...})`). This component
 * just renders.
 */
defineProps<{
  activities: Activity[];
  loading?: boolean;
  /** Text shown in the empty state. Defaults to a generic "No activity yet." message. */
  emptyText?: string;
}>();
</script>

<template>
  <div>
    <div v-if="loading && activities.length === 0" class="text-muted small">
      Loading…
    </div>
    <div v-else-if="activities.length === 0" class="text-muted small">
      <i class="bi bi-info-circle me-2"></i>
      {{ emptyText ?? "No activity yet." }}
    </div>
    <div v-else class="d-flex flex-column gap-3">
      <div
        v-for="activity in activities"
        :key="activity.id"
        class="d-flex"
        style="gap: 12px"
      >
        <div
          class="d-flex align-items-center justify-content-center rounded-circle bg-body-secondary fs-6"
          style="width: 32px; height: 32px;"
          :class="activityColorClass(activity.severity)"
        >
          <i :class="activityIconClass(activity)"></i>
        </div>
        <div class="flex-grow-1">
          <p class="mb-1" style="font-size: 14px; line-height: 1.4">
            {{ renderActivityTitle(activity) }}
          </p>
          <small class="text-muted">{{ formatActivityTimestamp(activity.occurredOn) }}</small>
        </div>
      </div>
    </div>
  </div>
</template>
